import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IDiagnosticOrder } from "@ispm/test/interfaces/diagnostic-order.interface";
import { RouterManagerService } from "@ispm/core/services/router-manager.service";
import { CoreActionCreatorService } from "@ispm/core/services/core-action-creator.service";
import { find, findIndex, get, isEmpty, isEqual } from 'lodash';
import { Observable, Subscription } from "rxjs";
import { NOTIFICATION_MESSAGE } from "@ispm-client/modules/aggregator/services/consts";
import { IfacillitiesInfo, Ifacillity } from "@ispm-client/modules/aggregator/interfaces";
import { ReadOnlyParameters } from "@ispm/test/interfaces/read-only-parameters";
import { Subject } from "rxjs/Subject";
import { WorkflowUtilService } from "@ispm-client/modules/aggregator/services/workflow-util.service";
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { Router } from "@angular/router";
import { OrderService } from "../../../../../modules/orders/services/order.service";
import { IManageFacilityAction } from '../../interfaces/manage-facility-action.interface';
import { AgregationService } from "@ispm-client/modules/aggregator/services";
import { InterpolateService } from "@ispm/shared/services/interpolate.service";
import { FormHandlerService } from "@ispm/dynamic-form/services/form-handler.service";
import { TestBarActionInterface } from '@ispm/test/components/test-bar/test-bar-action.interface';

@Component({
  selector: 'ispm-edit-managing-facility',
  templateUrl: './edit-managing-facility.component.html',
  styleUrls: ['./edit-managing-facility.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditManagingFacilityComponent implements OnInit, OnDestroy {

  @Input() currentTest: IDiagnosticOrder;
  @Input() testConfiguration: any;
  @Input() tooltipText: string = 'Edit managing facility';
  @Input() tooltipPosition: string = 'BOTTOM';
  @Input() manageFacilityAction: IManageFacilityAction | TestBarActionInterface;
  @Input() customCssClass: any;
  @Input() customStyleItem: any;
  @Input() readOnlyParameters: ReadOnlyParameters;

  private aggregatorData$: Subscription;
  private formId: string = 'EDIT-MANAGING-FACILITY-DEFAULT';
  private associatedFacilities: Ifacillity[] = [];
  public currentFacility: Ifacillity;
  public disabled: boolean = false;
  public isFacilityDriven: boolean = false;
  private destroyed$: Subject<any> = new Subject();

  constructor(private notificationMessageService: NotificationMessageService,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private _workflowService: WorkflowService,
              private formHandlerService: FormHandlerService,
              private aggregationService: AgregationService,
              private routerManagerService: RouterManagerService,
              private interpolateService: InterpolateService,
              private workflowUtilService: WorkflowUtilService,
              private router: Router,
              private coreActionCreator: CoreActionCreatorService,
              private orderService: OrderService) {
  }

  ngOnInit(): void {
    if (get(this.manageFacilityAction, 'payload.formId')) {
      this.formId = get(this.manageFacilityAction, 'payload.formId');
    }
    if (get(this.manageFacilityAction, 'payload.disableForTest')) {
      this.checkIfEditManagingFacilityDisable(get(this.manageFacilityAction, 'payload.disableForTest'));
    }
    this.formHandlerService.getForm(this.formId);
    this.fetchFacilityInfo();
  }

  checkIfEditManagingFacilityDisable(disableForTest: { evaluateValues: { path: string, expectedValue: any }[]}[]) {
    const testData = { ...this.currentTest };
    if (disableForTest && disableForTest.length > 0) {
      this.disabled = disableForTest.some(check => {
        return check.evaluateValues.every(val => {
          return isEqual(get(testData, val.path), val.expectedValue);
        });
      });
    }
  }
  /**
   * @method fetchFacilityInfo
   * @description Fetches the facility info for the current user
   */
  fetchFacilityInfo(): void {
    this.aggregationService.getFacilities()
      .takeUntil(this.destroyed$.asObservable())
      .subscribe((facilityInfo: IfacillitiesInfo) => {
        this.isFacilityDriven = facilityInfo.isFacilityEnabled;
        this.associatedFacilities = facilityInfo.currentAssociatedFacilities;
        if (get(this.currentTest, 'managingFacility', false) && facilityInfo.isFacilityEnabled) {
          const currentFacility = find(facilityInfo.currentFacilities, {id: get(this.currentTest, 'managingFacility')});
          if (currentFacility && currentFacility.name) {
            this.currentFacility = currentFacility;
          }
        }
      });
  }

  /**
   * @method getToolTipText
   * @description Dynamically handles the tooltip text base on the available data of current facility
   * @returns string
   */
  getToolTipText(): string {
    if (this.currentFacility && this.currentFacility.name) {
      return `${this.tooltipText} - Current: ${this.currentFacility.name}`;
    } else {
      return this.tooltipText;
    }
  }

  /**
   * @method editManagingFacility
   * @description Opens up the form in the popup modal
   */
  editManagingFacility(): void {
    this.createFormModal(
      this.formId,
      get(this.manageFacilityAction, 'payload.submitButtonText', 'Change'),
      get(this.manageFacilityAction, 'payload.cancelButtonText', 'Cancel'),
      get(this.manageFacilityAction, 'payload.titleText', 'Change Managing Facility')
    );
  }

  /**
   * @method createFormModal
   * @description Handles actions of the modal window with form config and data
   * @param currentFormId
   * @param approveButtonText
   * @param cancelButtonText
   * @param titleText
   */
  private createFormModal(currentFormId, approveButtonText, cancelButtonText, titleText): void {
    this.formHandlerService.popupDynamicFormModal(titleText, currentFormId, {}, approveButtonText, cancelButtonText)
      .take(1)
      .subscribe(data => {
        if (data['status'] === 1) {
          let formData: IDiagnosticOrder = get(data, 'payload.formData');
          if (isEmpty(formData) || (formData.managingFacility === this.currentTest.managingFacility)) {
            this.notificationMessageService.error(this.notificationMessage.NO_MODIFIED_DATA_FOR_MANAGING_FACILITY);
          } else {
            this.coreActionCreator.setLoader(true);
            this.aggregatorData$ = this.aggregationService
              .saveFormData(currentFormId, formData, [], 'proper', formData)
              .catch((err) => {
                this.notificationMessageService.error(this.notificationMessage.ERROR_IN_UPDATE_MANAGING_FACILITY);
                return Observable.of(err);
              })
              .subscribe((updatedData: any) => {
                  if (updatedData.code === 2) {
                    this.notificationMessageService.success(this.notificationMessage.UPDATE_MANAGING_FACILITY_SUCCESSFUL);
                    this.checkAndTriggerAcrValidation(get(updatedData, 'response.0.0'));
                    this.checkForReassignment(updatedData.response[0] || []);
                  } else if (updatedData.code === 3 || updatedData.code === 4) {
                    this.coreActionCreator.setLoader(false);
                    this.notificationMessageService.error(this.notificationMessage.ERROR_IN_UPDATE_MANAGING_FACILITY);
                  }
                },
                err => {
                  this.coreActionCreator.setLoader(false);
                  this.notificationMessageService.error(this.notificationMessage.ERROR_IN_UPDATE_MANAGING_FACILITY);
                  console.log(err);
                });
          }
        }
      });
  }
 //TODO create central interface for diagnosticOrder in core
  checkAndTriggerAcrValidation (diagnosticOrder: IDiagnosticOrder) {
    if (this.isFacilityDriven && get(this.testConfiguration, 'acr_validation')) {
      let payload;
      if (diagnosticOrder) {
        payload = { diagnosticOrder : { ...diagnosticOrder }, facilityChanged: true };
      }
      this.orderService.triggerAcrValidation(payload,true);
    }
  }


  /**
   * @method createFormModal
   * @description Validates if reassign is applicable and initiates it
   * @param data
   */
  private checkForReassignment(data: any[]) {
    const updatedOrder: IDiagnosticOrder = find(data, d => d.resourceType && d.resourceType.toLowerCase() === 'diagnosticorder');

    if (updatedOrder &&
      !isEmpty(get(updatedOrder, 'performer')) &&
      updatedOrder.performer.toLowerCase() !== 'none' &&
      findIndex(this.associatedFacilities, { id: updatedOrder.managingFacility}) === -1
    ) {
      const practitionerFilterBy = get(this.readOnlyParameters, 'practitionerFilterBy', undefined);
      const analysis = {
        activeRevision: this.routerManagerService.parameters?.revisionId,
        workflowrequest_id: this.routerManagerService.parameters?.testId,
        practitionerFilterBy
      };
      this._workflowService.reassignTest(analysis, 'None')
        .then(() => {
          this.handleRedirect(updatedOrder);
        })
        .catch(err => {
          console.log(err);
          this.coreActionCreator.setLoader(false);
          this.notificationMessageService.error(this.notificationMessage.ERROR_IN_UPDATE_MANAGING_FACILITY);
        });
    } else {
      this.handleRedirect(updatedOrder);
    }

  }

  /**
   * @method handleRedirect
   * @description handles if redirection is applicable and reloads the state accordingly
   * @param data
   */
  private handleRedirect(data) {
    localStorage.setItem('disableCache', "true");
    this.coreActionCreator.setLoader(false);
    if (get(this.manageFacilityAction, 'redirectUrl')) {
      this.handleNavigation(this.manageFacilityAction.redirectUrl, data);
    } else {
      this.routerManagerService.reload('', {reload: true});
    }
  }

  /**
   * @method handleNavigation
   * @param redirectUrl
   * @param data
   */
  private handleNavigation(redirectUrl: string, data: any) {
    const url = this.interpolateService.interpolate(redirectUrl, data);
    const uriState = this.routerManagerService.urlToState(url);
    this.router.navigate([uriState.name], {queryParams: uriState.params});
  }

  /**
   * @method ngOnDestroy
   */
  ngOnDestroy(): void {
    if (this.aggregatorData$) {
      this.aggregatorData$.unsubscribe();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
    this.formId = '';
  }

}
